// src/hooks/useElcCapByTech.js
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

const ELC_CAP_BY_TECH = gql`
  query ElcCapByTech($scenario: String!, $from: Int!, $to: Int!) {
    timeSeries(view: ELC_CAP, scenario: $scenario, fromYear: $from, toYear: $to) {
      year
      group   # process_set label from backend (e.g. EWND..., ESOL..., ENUCPWR...)
      value   # capacity, typically GW (set gwScale if different)
    }
  }
`;

// normalise once
const clean = (s) => String(s || "").trim().toUpperCase();

// Map your process_set (group) -> technology key
// Extend/refine as needed for your naming
function techKeyFor(group) {
  const g = clean(group);
  if (g.startsWith("EWND") || g.includes("WND") || g.includes("ON")) return "onshoreWind";
  if (g.startsWith("EWND") || g.includes("WND") || g.includes("OFF")) return "offshoreWind";
  if (g.startsWith("ESOL") || g.includes("SOLPV") || g.includes("SOL")) return "solar";
//   if (g.startsWith("EWAV") || g.includes("TIDAL")) return "tidal";
//   if (g.startsWith("EHYD") || g.includes("HYDRO")) return "hydro";
//   if (g.startsWith("EBIO") || g.includes("BIOMASS")) return "biomass";
//   if (g.startsWith("ENUCPWR") || g.includes("NUC")) return "nuclear";
  // drop unclassified by returning null (or map to "other" if you want it visible)
  return null;
}

export function useElcCapByTech({
  scenario,
  from = 2025,
  to = 2050,
  years = [2023, 2030, 2050], // pick the ticks you want to show
  gwScale = 1,               // if values are MW, set gwScale = 1/1000
} = {}) {
  const { data, loading, error } = useQuery(ELC_CAP_BY_TECH, {
    variables: { scenario, from, to },
    fetchPolicy: "cache-first",
  });

  // Aggregate capacity by tech + year
  const { labels, byTech, techList } = useMemo(() => {
    const rows = data?.timeSeries ?? [];
    const byTech = new Map(); // tech -> Map(year -> GW)

    for (const r of rows) {
      const tech = techKeyFor(r.group);
      if (!tech) continue;
      const y = Number(r.year);
      const v = (Number(r.value) || 0) * gwScale;
      if (!byTech.has(tech)) byTech.set(tech, new Map());
      byTech.get(tech).set(y, (byTech.get(tech).get(y) || 0) + v);
    }

    // Only keep requested labels (if a year missing, fill with 0)
    const labels = [...new Set(years)].sort((a, b) => a - b);

    const techList = Array.from(byTech.keys());
    return { labels, byTech, techList };
  }, [data, years, gwScale]);

  // Convenience totals
  const totalsByYear = useMemo(() => {
    return labels.map((y) =>
      Array.from(byTech.values()).reduce((sum, m) => sum + (m.get(y) || 0), 0)
    );
  }, [labels, byTech]);

  return { labels, byTech, techList, totalsByYear, loading, error };
}
